import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { DataSourceCard } from "@/components/ui/DataSourceCard";
import { Shield, Globe, Lock, Info } from "lucide-react";
import { Helmet } from "react-helmet-async";

const dataSources = [
  {
    icon: <Shield className="h-6 w-6" />,
    title: "OpenPhish Threat Feed",
    description: "We check every domain against OpenPhish, a widely-used, publicly verifiable feed of known phishing sites.",
    details: [
      "Detects domains involved in active phishing campaigns",
      "Updated continuously by independent security researchers",
      "A fully public feed, not a private or paid-only database",
      "New phishing sites are typically added within hours of being reported",
    ],
  },
  {
    icon: <Globe className="h-6 w-6" />,
    title: "Domain Registration Records",
    description: "We look up when the domain was registered and how long it has existed — because brand-new domains are more commonly used in scams.",
    details: [
      "Checks domain age — how old is this website?",
      "Uses standardized WHOIS and RDAP registry data",
      "Helps spot domains created hours or days ago",
      "Domain age alone doesn't prove anything — it's one signal among many",
    ],
  },
  {
    icon: <Lock className="h-6 w-6" />,
    title: "SSL & HTTPS Validation",
    description: "We verify whether the website uses encrypted connections — a basic security requirement that scam sites sometimes skip.",
    details: [
      "Checks if the site uses HTTPS (encrypted connection)",
      "Validates that the SSL certificate is active and properly issued",
      "Sites without HTTPS receive a higher risk score",
      "Having HTTPS alone doesn't mean the site is safe — it just means data is encrypted in transit",
    ],
  },
];

export default function DataSources() {
  return (
    <Layout>
      <Helmet>
        <title>Data Sources — How We Check Website Safety | Transparency Badge</title>
        <meta name="description" content="See exactly what data sources we use to check website safety — threat intelligence feeds, domain records, and SSL validation. Full transparency, no black boxes." />
        <meta property="og:title" content="Data Sources — How We Check Website Safety" />
        <meta property="og:description" content="Every data source we query — threat intelligence feeds, domain registration records, and SSL validation. No black boxes." />
        <meta property="og:url" content="https://transparencybadge.com/data-sources" />
        <link rel="canonical" href="https://transparencybadge.com/data-sources" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Full Transparency"
            title="Where Our Data Comes From"
            description="We don't hide how we evaluate websites. Here's every data source we use — and what each one tells us."
          />
        </div>
      </section>

      {/* Data Sources Grid */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {dataSources.map((source, index) => (
              <DataSourceCard
                key={index}
                icon={source.icon}
                title={source.title}
                description={source.description}
                details={source.details}
                className="animate-fade-in"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container max-w-3xl">
          <div className="bg-card rounded-xl border p-8">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Info className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Why We Check Multiple Sources — Not Just One
                </h3>
                <p className="text-muted-foreground mb-4">
                  No single data point tells the full story. A website can have HTTPS and still be a phishing site. A domain can be new and still be completely legitimate. That's why we combine multiple signals — threat data, domain history, and SSL status — into a single balanced result.
                </p>
                <p className="text-sm text-muted-foreground mb-4 bg-accent/50 rounded-lg px-4 py-3">
                  For the free checker, all checks are automatic. For the Verified Badge Program, applicants also pass a one-time eligibility check against our published criteria, followed by ongoing automated monitoring — a layer of confirmation no database alone can provide.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>A brand-new domain isn't automatically a scam — plenty of real businesses launch every day.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>HTTPS doesn't guarantee the site is trustworthy — scam sites can get certificates too.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Threat intelligence feeds aren't instant — new scam sites take time to get reported.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>That's why we combine everything — more signals mean a more accurate picture.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deep Explanation */}
      <section className="py-16 md:py-20 border-t">
        <div className="container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            Understanding Each Data Source
          </h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Not all website safety checks are created equal. Some tools check a single database and call it done. Others run one signal — like SSL — and display a green tick that gives users false confidence. We take a different approach: every check runs multiple independent data sources simultaneously, and the result is only as strong as the weakest signal in the combination.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Threat Intelligence Feeds: What They Track and How</h3>
            <p>
              Threat intelligence feeds are continuously updated databases of known malicious internet infrastructure. Security organizations around the world — including academic institutions, browser vendors, and independent researchers — operate these feeds as a public service. We query <strong>OpenPhish</strong>, a widely-used, publicly verifiable phishing intelligence feed. When a new phishing website goes live, it typically gets reported within hours. When malware is spotted distributing through a particular domain, that domain gets flagged.
            </p>
            <p>
              These feeds don't catch everything, and they're not instant. A brand-new scam site might operate for 24 to 48 hours before it appears in threat databases. That lag is a known limitation of any automatic system. It's one reason we also check domain age — because a site that's only 48 hours old is suspicious regardless of whether it's been reported yet. We only use publicly maintained, openly verifiable feeds. Everything we check is data you could verify independently.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Domain Registration Records: The Age Signal Explained</h3>
            <p>
              Every domain name has a public registration record showing when it was first registered. This data is accessible through WHOIS and RDAP protocols — standardized systems that allow anyone to look up domain history. When you check a website through our tool, we query these records to determine how long the domain has existed.
            </p>
            <p>
              Domain age matters for a specific reason: scammers almost always use new domains. Running a long-term scam operation on the same domain is risky because reports, chargebacks, and fraud complaints accumulate. Instead, scammers register fresh domains for each campaign, run it for days or weeks, then move on. An established domain — registered for a year or more with no incident — is meaningfully less likely to be a scam than one registered last week.
            </p>
            <p>
              What domain age cannot tell you: whether the business behind the website is honest, whether the site has been hacked since it was established, or whether the business claims are real. A legitimate business can have a new domain, and a scam can occasionally operate from an older one. Age is a signal, not a verdict.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">SSL Certificate Validation: The Padlock Myth Debunked</h3>
            <p>
              SSL certificates protect data in transit between your browser and a web server. When a site uses HTTPS, it means your connection is encrypted — your data can't be intercepted while it travels over the network. For years, security educators taught people to look for the padlock as a sign of safety. That advice is now outdated.
            </p>
            <p>
              Free SSL certificates became widely available in 2016 through services like Let's Encrypt. Today, virtually every website — legitimate or malicious — can have HTTPS enabled within minutes at no cost. The padlock no longer tells you anything about the trustworthiness of the website itself. We still check SSL because its absence is a serious red flag. But its presence is not a positive trust signal — it's just the baseline we'd expect from any website.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Why We Publish This Information</h3>
            <p>
              Most website safety tools operate as black boxes. They give you a score or a color without explaining what went into it. We publish our methods because we think users deserve to understand what a result actually means. If you know that our "Established" result comes from domain age plus SSL plus no threat matches, you can correctly interpret it: this domain isn't new and hasn't been reported. It does not mean someone personally vouched for the business.
            </p>
            <p>
              That distinction matters when you're deciding whether to enter payment details. Full transparency about our methods also means full transparency about our limitations — and we publish both.
            </p>
          </div>
        </div>
      </section>

      {/* Data Privacy */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            What We Don't Collect
          </h2>
          <p className="text-muted-foreground mb-8">
            Our checker only processes the domain name you enter. We never access, store, or transmit:
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              "Your personal information",
              "Your browsing history",
              "Cookies or tracking data",
            ].map((item, index) => (
              <div
                key={index}
                className="bg-card rounded-lg border px-4 py-3 text-sm font-medium text-foreground"
              >
                ✕ {item}
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
